<template>
  <button
    :class="[variant === 'outline' ? 'button-outline' : 'button', { 'is-loading': loading }]"
    :disabled="disabled || loading"
    :aria-busy="loading"
    :type="type"
  >
    <!-- the label stays in place while loading so the width never jumps -->
    <span class="button-loading__content">
      <slot />
    </span>

    <span class="button-loading__loader">
      <span class="button-loading__spinner"></span>
    </span>
  </button>
</template>

<script setup>
const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  // a prop rather than a fallthrough attribute, which would override the
  // loading lock
  disabled: {
    type: Boolean,
    default: false,
  },
  //   ! check -> remove
  success: {
    type: Boolean,
  },
  type: {
    type: String,
    default: "button",
  },
  color: {
    type: String,
    default: "var(--general-color)",
  },
  // "outline" leaves the look to the parent (the buttons of table.scss) and
  // only adds the loading state
  variant: {
    type: String,
    default: "filled",
    validator: (v) => ["filled", "outline"].includes(v),
  },
});
</script>

<style lang="scss" scoped>
.button {
  position: relative;
  width: 198px;
  height: 43px;
  background: v-bind("props.color");
  border: 1px solid v-bind("props.color");
  color: #ffffff;
  user-select: none;
  border-radius: 11px;
  font-family: Gilroy;
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 17px;
  text-align: center;
  text-transform: uppercase;
  cursor: pointer;
  transition-property: background, color, opacity;
  transition-duration: 0.3s;

  // :focus would keep the hover look after a click until the next click
  // elsewhere; only keyboard focus needs it
  &:focus-visible,
  &:hover {
    background: transparent;
    color: v-bind("props.color");
  }
  &:focus-visible {
    outline: 1px solid v-bind("props.color");
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background: var(--border-primary-color);
    border-color: var(--border-primary-color);
    color: var(--block-color);
    border-width: 2px;
    &:hover {
      outline: none;
    }
  }

  // busy, not off: the button keeps its colour and only dims a little
  &.is-loading,
  &.is-loading:hover,
  &.is-loading:focus-visible {
    opacity: 0.75;
    background: v-bind("props.color");
    border-color: v-bind("props.color");
    border-width: 1px;
    color: #ffffff;
    outline: none;
  }
}

// a separate root class keeps .button and the size overrides parents put on
// it at the same specificity
.button-outline {
  position: relative;

  &.is-loading {
    opacity: 0.75;
    // no hover fill under a busy button
    pointer-events: none;
  }
}

.button-loading__content,
.button-loading__loader {
  transition: opacity 0.2s ease;
}

.button-loading__loader {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
}

.is-loading .button-loading__content {
  opacity: 0;
}

.is-loading .button-loading__loader {
  opacity: 1;
}

// the ring of the global .spinner, in white so it reads on the button colour
.button-loading__spinner {
  display: block;
  width: 1.5em;
  height: 1.5em;
  font-size: 16px;
  border-radius: 50%;
  border: 0.215em solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  animation: spin 1s cubic-bezier(0.55, 0.15, 0.45, 0.85) infinite;
}

// on a white outline button the ring takes the status colour of the text
.button-outline .button-loading__spinner {
  width: 20px;
  height: 20px;
  border-width: 3px;
  border-color: rgba(0, 0, 0, 0.12);
  border-color: color-mix(in srgb, currentColor 30%, transparent);
  border-top-color: currentColor;
}

@media (prefers-reduced-motion: reduce) {
  .button-loading__content,
  .button-loading__loader {
    transition: none;
  }
}
</style>
